import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Receipt, User, Phone, Mail, CreditCard, CheckCircle2, Building, Calendar, MapPin } from 'lucide-react';
import InvoicePaymentForm from '@/components/public/InvoicePaymentForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NOTE (client-facing English only): this page is opened by the CUSTOMER,
// never by an internal CRM user — its copy must NEVER depend on the admin's
// language toggle (es/en) or browser locale. A local hardcoded dictionary
// (instead of react-i18next's useTranslation) guarantees English always.
const EN = {
  publicInvoice: {
    notFound: 'Invoice not found or invalid link',
    invoiceDetails: 'Invoice Details',
    description: 'Description', quantity: 'Qty', price: 'Price', total: 'Total',
    subtotal: 'Subtotal', notes: 'Notes', dueDate: 'Due Date', overdue: 'Invoice overdue',
    cardSurcharge: 'Card surcharge (4%)', amountPaid: 'Paid', balanceDue: 'Balance Due', totalPaid: 'Total Paid',
    invoicePaid: 'Invoice Paid', invoicePaidMessage: 'This invoice has been paid in full. Thank you.',
    paymentSuccessful: "Payment processed. In production mode you will be redirected to Stripe's secure form.",
    paymentFailed: 'Error processing payment',
    paymentServiceUnavailable: 'Payment service unavailable at this time.',
    processing: 'Processing...', payNow: 'Pay',
    securePayment: 'Secure payment processed by Stripe',
    payWithCardAch: 'Pay with card or bank account (ACH)',
    companyFooter: 'Blue Well Drilling, LLC — Florida',
    statuses: { draft: 'Draft', sent: 'Sent', partial: 'Partial', paid: 'Paid', overdue: 'Overdue', cancelled: 'Cancelled' },
  },
};
const t = (key) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), EN) ?? key;

function StatusBadge({ status }) {
  const STATUS_CONFIG = {
    'draft': { label: t('publicInvoice.statuses.draft'), class: 'bg-gray-100 text-gray-600' },
    'sent': { label: t('publicInvoice.statuses.sent'), class: 'bg-blue-100 text-blue-700' },
    'partial': { label: t('publicInvoice.statuses.partial'), class: 'bg-amber-100 text-amber-700' },
    'paid': { label: t('publicInvoice.statuses.paid'), class: 'bg-emerald-100 text-emerald-700' },
    'overdue': { label: t('publicInvoice.statuses.overdue'), class: 'bg-red-100 text-red-700' },
    'cancelled': { label: t('publicInvoice.statuses.cancelled'), class: 'bg-gray-100 text-gray-500' }
  };
  const config = STATUS_CONFIG[status] || { label: status, class: 'bg-gray-100 text-gray-600' };
  return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.class}`}>{config.label}</span>;
}

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);

  const loadInvoice = () =>
    fetch(`${API_URL}/api/invoices/public/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => setData(d))
      .catch(() => {
        setError(t('publicInvoice.notFound'));
        fetch(`${API_URL}/api/settings/company/public`).then(r => r.json()).then(setCompanyInfo).catch(() => {});
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    loadInvoice();
    // Detect return from a redirect-based payment method (e.g. ACH).
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    if (redirectStatus === 'succeeded' || redirectStatus === 'processing') {
      setPaymentStatus({ type: 'success', message: t('publicInvoice.paymentSuccessful') });
    } else if (redirectStatus === 'failed') {
      setPaymentStatus({ type: 'error', message: t('publicInvoice.paymentFailed') });
    }
  }, [token]);

  const handlePaid = () => {
    setPaymentStatus({ type: 'success', message: t('publicInvoice.paymentSuccessful') });
    // Webhook applies the payment server-side; refetch shortly to reflect it.
    setTimeout(loadInvoice, 2500);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-md w-full" data-testid="public-invoice-error-card">
        <CardContent className="p-8 text-center">
          <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-700 font-semibold">{error}</p>
          <p className="text-sm text-gray-500 mt-2">
            {t('publicInvoice.notFoundHelp', { defaultValue: 'We could not load this invoice. Please contact us and we will help you complete your payment.' })}
          </p>
          <div className="mt-5 flex flex-col items-center gap-2">
            <a href={`tel:${(companyInfo?.phone || '(863) 658-0035').replace(/[^0-9+]/g, '')}`} data-testid="public-invoice-error-phone" className="inline-flex items-center gap-2 text-[#003366] font-semibold hover:underline">
              <Phone className="w-4 h-4" /> {companyInfo?.phone || '(863) 658-0035'}
            </a>
            <a href={`mailto:${companyInfo?.email || 'info@bluewell.pro'}`} data-testid="public-invoice-error-email" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:underline">
              <Mail className="w-3.5 h-3.5" /> {companyInfo?.email || 'info@bluewell.pro'}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const { invoice, customer } = data;
  const paymentRequest = data.payment_request || null;
  const balance = paymentRequest ? paymentRequest.amount : ((invoice.total || 0) - (invoice.amount_paid || 0));
  const isPaid = paymentRequest ? paymentRequest.status === 'paid' : (invoice.status === 'paid' || balance <= 0);

  // Fix H (2026-09-01): same gap as the estimate's public page — Job Site
  // Address only showed the street + county, never city/state/zip, even
  // though the invoice document already has well_city/well_state/well_zip.
  // No `project` object is returned by this endpoint, so there's no 3rd
  // fallback level here (unlike the estimate page).
  const jobCity = invoice.well_city || invoice.location?.city || '';
  const jobState = invoice.well_state || invoice.location?.state || '';
  const jobZip = invoice.well_zip || invoice.location?.zip_code || '';
  const jobCountyRaw = invoice.property_info?.county || invoice.location?.county || '';
  const jobCounty = jobCountyRaw ? (/county/i.test(jobCountyRaw) ? jobCountyRaw : `${jobCountyRaw} County`) : '';

  return (
    <div data-testid="public-invoice-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#001a33] to-[#003366] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-lg font-bold font-['Barlow']">BW</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold font-['Barlow']">Blue Well Drilling</h1>
              <p className="text-blue-200 text-xs">Invoice {invoice.invoice_number}</p>
            </div>
          </div>
          <StatusBadge status={invoice.status} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Paid Banner */}
        {isPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">{t('publicInvoice.invoicePaid')}</p>
              <p className="text-xs text-emerald-600">{t('publicInvoice.invoicePaidMessage')}</p>
            </div>
          </div>
        )}

        {/* Payment Status */}
        {paymentStatus && (
          <div className={`border rounded-xl p-4 flex items-center gap-3 ${paymentStatus.type === 'success' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <CreditCard className={`w-6 h-6 flex-shrink-0 ${paymentStatus.type === 'success' ? 'text-blue-500' : 'text-red-500'}`} />
            <p className={`text-sm ${paymentStatus.type === 'success' ? 'text-blue-700' : 'text-red-700'}`}>{paymentStatus.message}</p>
          </div>
        )}

        {/* Payment Request Banner */}
        {paymentRequest && !isPaid && (
          <div data-testid="payment-request-banner" className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-500 font-semibold mb-1">Payment Request</p>
                <p className="text-lg font-bold text-blue-900">{paymentRequest.label || 'Payment'}</p>
                {paymentRequest.notes && <p className="text-sm text-blue-700 mt-1">{paymentRequest.notes}</p>}
                {paymentRequest.due_date && <p className="text-xs text-blue-500 mt-1">Due: {paymentRequest.due_date.slice(0, 10)}</p>}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-900">${(paymentRequest.amount || 0).toFixed(2)}</p>
                <p className="text-xs text-blue-500 mt-1">of ${(invoice.total || 0).toFixed(2)} total</p>
                <p className="text-xs text-blue-400">Paid so far: ${(invoice.amount_paid || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Bill To / Mailing Address + Job Site Address */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {customer && (
            <Card className="bg-white"><CardContent className="p-4">
              <p data-testid="invoice-mailing-address-label" className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2 flex items-center gap-1"><User className="w-3 h-3" /> Bill To / Mailing Address</p>
              <p className="text-sm font-semibold text-gray-900">{customer.first_name} {customer.last_name}</p>
              {customer.company_name && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Building className="w-3 h-3" />{customer.company_name}</p>}
              {invoice.mailing_address?.address ? (
                <>
                  <p className="text-xs text-gray-500 mt-1">{invoice.mailing_address.address}</p>
                  {(invoice.mailing_address.city || invoice.mailing_address.state || invoice.mailing_address.zip) && (
                    <p className="text-xs text-gray-500">
                      {[invoice.mailing_address.city, invoice.mailing_address.state].filter(Boolean).join(', ')} {invoice.mailing_address.zip || ''}
                    </p>
                  )}
                </>
              ) : customer.address ? (
                <p className="text-xs text-gray-500 mt-1">{customer.address}</p>
              ) : null}
            </CardContent></Card>
          )}
          <Card className="bg-white"><CardContent className="p-4">
            <p data-testid="invoice-job-site-label" className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Job Site Address</p>
            {invoice.well_address ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{invoice.well_address}</p>
                {(jobCity || jobState || jobZip) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {[jobCity, jobState].filter(Boolean).join(', ')} {jobZip}
                  </p>
                )}
                {jobCounty && <p className="text-xs text-gray-500">{jobCounty}</p>}
                {invoice.property_info?.parcel_id && <p className="text-xs text-gray-500 font-mono">Parcel ID: {invoice.property_info.parcel_id}</p>}
              </>
            ) : (
              <>
                {invoice.due_date && (
                  <>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('publicInvoice.dueDate')}</p>
                    <p className="text-sm font-semibold text-gray-900">{new Date(invoice.due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    {invoice.status === 'overdue' && <p className="text-xs text-red-500 font-medium mt-1">{t('publicInvoice.overdue')}</p>}
                  </>
                )}
              </>
            )}
          </CardContent></Card>
        </div>

        {/* Line Items */}
        <Card className="bg-white">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">{t('publicInvoice.invoiceDetails')}</h3>
            </div>
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">{t('publicInvoice.description')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicInvoice.quantity')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicInvoice.price')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicInvoice.total')}</th>
              </tr></thead>
              <tbody>
                {(invoice.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{item.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">${(item.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">${(item.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-4 bg-gray-50 space-y-1">
              <div className="flex justify-between text-sm text-gray-500">
                <span>{t('publicInvoice.subtotal')}</span>
                <span className="font-medium text-gray-700">${(invoice.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {invoice.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Discount</span>
                  <span className="font-medium">-${(invoice.discount_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {invoice.surcharge > 0 && (
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{t('publicInvoice.cardSurcharge')}</span>
                  <span className="font-medium text-gray-700">${(invoice.surcharge || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{t('publicInvoice.amountPaid')}</span>
                  <span className="font-medium text-emerald-600">-${(invoice.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-base font-bold text-[#003366]">
                  {isPaid ? t('publicInvoice.totalPaid') : t('publicInvoice.balanceDue')}
                </span>
                <span className="text-lg font-bold text-[#003366]">
                  ${(isPaid ? invoice.total : balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {invoice.notes && (
          <Card className="bg-white"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">{t('publicInvoice.notes')}</p>
            <p className="text-sm text-gray-700">{invoice.notes}</p>
          </CardContent></Card>
        )}

        {/* Pay (card / ACH via Stripe) */}
        {!isPaid && paymentStatus?.type !== 'success' && (
          <InvoicePaymentForm
            invoice={invoice}
            balance={balance}
            amountLabel={`$${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            onPaid={handlePaid}
            t={t}
          />
        )}

        <div className="text-center pb-8">
          <p className="text-xs text-gray-300">{t('publicInvoice.companyFooter')}</p>
        </div>
      </div>
    </div>
  );
}
